/**
 * Firmware limits, transcribed from spec §3 and §10.
 *
 * These are the DEVICE's limits — they are not safety policy and must never be
 * loosened. Safety policy lives in safety.ts as adjustable guard rails.
 */

export const LIMITS = {
  cycles: { min: 1, max: 7 },
  onMin: { min: 1, max: 480 },
  offMin: { min: 0, max: 480 },
  carrierHz: { min: 100, max: 20000 },
  intensity: { min: 0, max: 100 },
  gateHz: { min: 0.5, max: 200 },
  sweepHold: { min: 1, max: 60 },
  nameLen: { min: 1, max: 12 },
  gateDutyPct: { min: 1, max: 100 },
} as const;

/** UI slider steps. Suggestions from spec §10, not firmware constraints. */
export const STEPS = {
  carrierHz: 50,
  gateHz: 0.5,
  intensity: 1,
  minutes: 1,
  sweepHold: 1,
} as const;

/**
 * Recommended carrier default.
 *
 * The firmware default of 1000 Hz is suboptimal on every metric except heat.
 * With the coil at ~1.5 mH / 2.5 ohm, modulation depth is -3 dB at 474 Hz and
 * -6 dB at 760 Hz — so at 1000 Hz most of the current swing is already gone.
 * Above ~2 kHz the coil behaves as a DC electromagnet switched by the gate.
 *
 * NOTE: the 1.5 mH figure is CALCULATED (Wheeler's formula), not measured.
 * Phase 0 task #1 covers the LCR meter reading. All characteristic frequencies
 * here scale as 1/L, so revisit this constant once measured.
 */
export const RECOMMENDED_CARRIER_HZ = 300;

/** Practical carrier span. Outside this the control does little but heat the coil. */
export const USEFUL_CARRIER_RANGE = { lo: 100, hi: 1000 } as const;

/**
 * Device command pacing. Spec §2.4 requires 250-300 ms between commands when
 * sending a program sequence.
 */
export const COMMAND_DELAY_MS = 300;

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Round to a step, then clamp. Used by every slider. */
export function quantize(value: number, step: number, min: number, max: number): number {
  const snapped = Math.round(value / step) * step;
  // Floating point: 0.1 + 0.2 style drift shows up badly on 0.5 Hz gate steps.
  const cleaned = Number(snapped.toFixed(4));
  return clamp(cleaned, min, max);
}

/* ------------------------------------------------------------------ *
 * Duty cycle
 * ------------------------------------------------------------------ */

/**
 * What a setting actually does to the coil.
 *
 * From firmware v4.1, INTENSITY sets carrier duty one-for-one — 100 means the
 * output is held high for the whole pulse. So at INTENSITY 100 the share of
 * time the field is on IS the pulse/rest setting, with nothing else folded in.
 *
 * Below 100 the carrier chops within each pulse and the two multiply, which is
 * why the readout shows both numbers.
 */
export function dutyCycle(
  intensityPct: number,
  gateDutyPct = 50,
): { carrier: number; average: number } {
  return {
    carrier: Number(intensityPct.toFixed(1)),
    average: Number((intensityPct * (gateDutyPct / 100)).toFixed(1)),
  };
}

/**
 * Default intensity for a new step.
 *
 * Full, so the pulse is a clean square and the pulse/rest setting is the only
 * thing deciding how much of the time the field is on.
 */
export const DEFAULT_INTENSITY = 100;

/** Pulse-on share for a new step, percent. */
export const DEFAULT_GATE_DUTY = 20;
